/**
 * Iter114 — Bulk camt.053 Drop-Zone für Treuhänder-Cockpit
 *
 * Drag&Drop oder File-Picker, dann Live-Vorschau pro Datei (Zielfirma, Anzahl Einträge),
 * gefolgt von „Bestätigen & Importieren". Auto-Routing per IBAN.
 */
import React, { useCallback, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UploadCloud, FileWarning, CheckCircle2, Loader2, X } from "lucide-react";

export default function BulkCamtDropZone({ onImported }) {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]); // File[]
  const [preview, setPreview] = useState(null); // BulkPreviewResponse
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setPreview(null);
    setResult(null);
  };

  const handleFiles = useCallback(async (fileList) => {
    const arr = Array.from(fileList).filter((f) => /\.(xml|zip)$/i.test(f.name));
    if (arr.length === 0) {
      toast.error("Keine XML- oder ZIP-Dateien gefunden.");
      return;
    }
    setFiles(arr);
    setResult(null);

    const fd = new FormData();
    arr.forEach((f) => fd.append("files", f));
    setPreviewLoading(true);
    try {
      const res = await apiClient.post("/bank-import-camt/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files?.length) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => setDragActive(false);

  const onPick = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
  };

  const confirmImport = async () => {
    if (!files.length) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await apiClient.post("/bank-import-camt/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(
        `${res.data.total_entries} Bank-Einträge importiert (${res.data.per_company.length} Firma(en))`,
      );
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="p-4 bg-white border border-slate-200 shadow-sm" data-testid="bulk-camt-dropzone-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <UploadCloud className="h-4 w-4 text-violet-600" />
            Bulk camt.053-Import (Multi-Mandant)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Mehrere Bank-Statement-Dateien gleichzeitig hochladen — werden automatisch per IBAN
            in die richtige Firma einsortiert.
          </p>
        </div>
        {(preview || result) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            data-testid="bulk-camt-reset"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {!preview && !result && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => inputRef.current?.click()}
          data-testid="bulk-camt-dropzone"
          className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            dragActive
              ? "border-violet-500 bg-violet-50"
              : "border-slate-300 bg-slate-50 hover:border-violet-400 hover:bg-slate-50"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xml,.zip,application/zip,application/x-zip-compressed"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid="bulk-camt-file-input"
          />
          <UploadCloud className="h-8 w-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-700">
            <strong>Dateien hierher ziehen</strong> oder klicken, um zu wählen
          </p>
          <p className="text-xs text-slate-500 mt-1">camt.053-XML oder ZIP mit mehreren camt-Dateien — mehrere gleichzeitig möglich</p>
          {previewLoading && (
            <p className="mt-2 text-xs text-slate-600 flex items-center justify-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Lade Vorschau …
            </p>
          )}
        </div>
      )}

      {/* Vorschau */}
      {preview && !result && (
        <div className="space-y-3" data-testid="bulk-camt-preview">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="rounded bg-emerald-50 border border-emerald-200 p-2">
              <div className="text-emerald-700">Routbar</div>
              <div className="text-lg font-semibold text-emerald-900">{preview.routable_count}</div>
            </div>
            <div className="rounded bg-amber-50 border border-amber-200 p-2">
              <div className="text-amber-700">Nicht zuordenbar</div>
              <div className="text-lg font-semibold text-amber-900">{preview.unroutable_count}</div>
            </div>
            <div className="rounded bg-slate-50 border border-slate-200 p-2">
              <div className="text-slate-600">Einträge total</div>
              <div className="text-lg font-semibold text-slate-900">{preview.total_entries}</div>
            </div>
          </div>

          <ul className="space-y-1 max-h-60 overflow-y-auto" data-testid="bulk-camt-preview-list">
            {preview.files.map((f, i) => (
              <li
                key={i}
                className={`text-xs rounded px-2 py-1.5 border flex items-start gap-2 ${
                  f.error
                    ? "bg-amber-50 border-amber-200"
                    : "bg-emerald-50 border-emerald-200"
                }`}
                data-testid={`bulk-camt-preview-row-${i}`}
              >
                {f.error ? (
                  <FileWarning className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{f.filename}</div>
                  {f.error ? (
                    <div className="text-amber-700">{f.error}</div>
                  ) : (
                    <div className="text-slate-600">
                      → <strong>{f.target_company_name}</strong> ({f.entry_count} Einträge, IBAN {f.detected_iban})
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="flex justify-between items-center gap-2">
            <p className="text-xs text-slate-500">
              {preview.unroutable_count > 0 && "Nicht zuordenbare Dateien werden übersprungen."}
            </p>
            <Button
              onClick={confirmImport}
              disabled={importing || preview.routable_count === 0}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="bulk-camt-confirm-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importiere…
                </>
              ) : (
                `${preview.routable_count} Datei(en) importieren`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Ergebnis */}
      {result && (
        <div className="space-y-2" data-testid="bulk-camt-result">
          <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-sm">
            <div className="font-semibold text-emerald-900">
              ✓ {result.total_entries} Bank-Einträge in {result.per_company.length} Firma(en) importiert
            </div>
          </div>
          {result.per_company.length > 0 && (
            <ul className="space-y-1" data-testid="bulk-camt-result-list">
              {result.per_company.map((r) => (
                <li
                  key={r.company_id}
                  className="text-xs flex justify-between items-center bg-white border border-slate-200 rounded px-2 py-1.5"
                >
                  <span className="font-medium">{r.company_name}</span>
                  <span className="text-slate-600">
                    {r.files_imported} Datei(en) · {r.entries_imported} Einträge
                    {r.auto_matched_count > 0 && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-800">
                        {r.auto_matched_count} auto-matched
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {result.skipped_unroutable?.length > 0 && (
            <div className="text-xs text-amber-700">
              <strong>Übersprungen ({result.skipped_unroutable.length}):</strong>{" "}
              {result.skipped_unroutable.join("; ")}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
